import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dropdown, Tooltip } from 'antd';
import {
    HomeOutlined,
    FolderOutlined,
    TeamOutlined,
    MessageOutlined,
    CalendarOutlined,
    ClockCircleOutlined,
    FileTextOutlined,
    UsergroupAddOutlined,
    DollarOutlined,
    CreditCardOutlined,
    SafetyOutlined,
    UserOutlined,
    SettingOutlined,
    DownOutlined,
    PlusOutlined,
    MinusOutlined,
    MoreOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    ShopOutlined,
    RiseOutlined,
    ApartmentOutlined,
    PushpinOutlined,
    RightOutlined,
    AppstoreOutlined,
    DashboardOutlined,
    BuildOutlined,
    ProjectOutlined,
} from '@ant-design/icons';
import { useCanCheck } from '@/ability';
import { useAuthStore } from '@stores/auth.store';
import CreateBugReportModal from '@/components/bug-report/CreateBugReportModal';
import useUIStore from '@stores/ui.store';
import usePinnedProjects from '@hooks/projects/usePinnedProjects';
import { Pin } from 'lucide-react';
import logoSrc from '@assets/logo.png';

// 100% REAL Pages in Sprinto Web App with Permission Metadata
const RAW_NAV_ITEMS = [
    { key: '/tenant/dashboard', label: 'Dashboard', icon: HomeOutlined, section: null },
    { key: '/tenant/open-market/overview', label: 'Open Market', icon: ShopOutlined, section: null, requiredPermission: ['open_market.leads.view', 'open_market.bids.view', 'open_market.leads.create', 'open_market.bids.create'] },
    { key: '/tenant/chat', label: 'Chat', icon: MessageOutlined, section: null, requiredPermission: ['chat.', 'chat.messages.view', 'chat.conversations.view', 'chat.messages.create', 'chat.rooms.view', 'chat.room.join', 'chat.room.create', 'chat.messages.with_clients'] },

    // PMO (PROJECT MANAGEMENT OFFICE)
    { key: '/tenant/projects', label: 'Project', icon: FolderOutlined, section: 'PMO', requiredPermission: ['projects.projects.view', 'projects.details.view', 'projects.overview.view', 'projects.directory.view'] },
    { key: '/tenant/projects/sprints', label: 'Sprint/Task', icon: CalendarOutlined, section: 'PMO', requiredPermission: ['projects.sprints.view', 'projects.tasks.view'] },
    { key: '/tenant/projects/kanban', label: 'Kanban', icon: ProjectOutlined, section: 'PMO', requiredPermission: ['projects.tasks.view', 'projects.sprints.view'] },
    { key: '/tenant/projects/build', label: 'Build', icon: BuildOutlined, section: 'PMO', requiredPermission: ['projects.builds.view', 'projects.builds.view_all'] },
    { key: '/tenant/projects/release', label: 'Release', icon: ApartmentOutlined, section: 'PMO', requiredPermission: ['projects.milestone_submissions.submit', 'projects.pmo.view', 'projects.completion_requests.view'] },

    // HUMAN RESOURCE
    { key: '/tenant/hr/overview', label: 'Overview', icon: ApartmentOutlined, section: 'HUMAN RESOURCE', requiredPermission: ['hr.employees.view', 'hr.employees.view_all'], hrOnlyWithEmployeeView: true },
    { key: '/tenant/hr/employees', label: 'Employees', icon: TeamOutlined, section: 'HUMAN RESOURCE', requiredPermission: ['hr.employees.view', 'hr.employees.view_all'], hrOnlyWithEmployeeView: true },
    { key: '/tenant/hr/schedules', label: 'Schedules', icon: CalendarOutlined, section: 'HUMAN RESOURCE', requiredPermission: 'hr.schedule.view', hrOnlyWithEmployeeView: true },
    { key: '/tenant/hr/attendance', label: 'Attendance', icon: ClockCircleOutlined, section: 'HUMAN RESOURCE', requiredPermission: ['hr.attendance.view', 'hr.attendance.view_all'], hrOnlyWithEmployeeView: true },
    { key: '/tenant/hr/leave', label: 'Leaves', icon: FileTextOutlined, section: 'HUMAN RESOURCE', requiredPermission: ['hr.leave.view', 'hr.leaves.view', 'hr.leave.request', 'hr.leave.approve'], hrOnlyWithEmployeeView: true },
    { key: '/tenant/hr/request', label: 'Requests', icon: FileTextOutlined, section: 'HUMAN RESOURCE', requiredPermission: ['hr.request.view', 'hr.request.view_all', 'hr.request.create'] },
    { key: '/tenant/hr/performance', label: 'Performance', icon: RiseOutlined, section: 'HUMAN RESOURCE', requiredPermission: 'hr.performance.view', hrOnlyWithEmployeeView: true },

    // FINANCE
    { key: '/tenant/hr/salary', label: 'Salary Distribution', icon: DollarOutlined, section: 'FINANCE', requiredPermission: 'hr.salary.view' },
    { key: '/tenant/billing/requests', label: 'Billing & Requests', icon: CreditCardOutlined, section: 'FINANCE', requiredPermission: 'billing.requests.view' },
    { key: '/tenant/sales/leads', label: 'Sales Leads', icon: RiseOutlined, section: 'FINANCE', requiredPermission: ['sales.leads.view', 'sales.leads_client.view'] },

    // BUSINESS & ACCESS
    { key: '/tenant/clients', label: 'Clients', icon: UsergroupAddOutlined, section: 'BUSINESS', requiredPermission: 'clients.clients.view' },
    { key: '/tenant/roles', label: 'Roles & Permissions', icon: SafetyOutlined, section: 'BUSINESS', requiredPermission: 'saas.roles.view' },
    { key: '/tenant/users', label: 'System Users', icon: UserOutlined, section: 'BUSINESS', requiredPermission: 'saas.users.view' },
];

function ProjectsNavItem({ item, active, isExpanded, effectiveCollapsed, handleNavClick, pinnedProjects, navigate }) {
    const [hovered, setHovered] = useState(false);
    const IconComp = item.icon;

    const pinnedMenu = {
        items: pinnedProjects.length === 0
            ? [
                {
                    key: 'empty',
                    label: (
                        <div style={{ fontSize: 12, color: '#94a3b8', padding: '6px 12px' }}>
                            No pinned projects
                        </div>
                    ),
                    disabled: true,
                },
              ]
            : [
                {
                    key: 'header',
                    label: (
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', padding: '4px 12px 2px', letterSpacing: '0.5px' }}>
                            Pinned Projects ({pinnedProjects.length})
                        </div>
                    ),
                    disabled: true,
                },
                { type: 'divider' },
                ...pinnedProjects.map((p) => ({
                    key: p.id,
                    label: (
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/tenant/projects/${p.id}`);
                            }}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px', borderRadius: 4 }}
                        >
                            <PushpinOutlined style={{ fontSize: 12, color: '#7c3aed' }} />
                            <span style={{ fontSize: 12, fontWeight: 500, color: '#334155' }}>{p.name || p.title}</span>
                        </div>
                    ),
                })),
              ],
    };

    const buttonContent = (
        <button
            type="button"
            onClick={() => handleNavClick(item)}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                width: '100%',
                height: 36,
                padding: '0 6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: effectiveCollapsed ? 'center' : 'flex-start',
                gap: 10,
                border: 'none',
                borderRadius: 8,
                background: active ? '#f5f3ff' : hovered ? '#f8fafc' : 'transparent',
                color: active ? '#7c3aed' : '#475569',
                fontWeight: active ? 600 : 500,
                fontSize: 12.5,
                cursor: 'pointer',
                transition: 'background 0.15s ease',
                outline: 'none',
            }}
        >
            <div
                style={{
                    width: 28,
                    height: 28,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                <IconComp
                    style={{
                        fontSize: 16,
                        color: active ? '#7c3aed' : '#64748b',
                    }}
                />
            </div>
            {!effectiveCollapsed && (
                <span
                    style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        flex: 1,
                        textAlign: 'left',
                    }}
                >
                    {item.label}
                </span>
            )}
            {!effectiveCollapsed && item.children && item.children.length > 0 && (
                <DownOutlined
                    style={{
                        fontSize: 9,
                        color: active ? '#7c3aed' : '#94a3b8',
                        transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: 'transform 0.2s ease',
                        marginRight: 4,
                    }}
                />
            )}
            {!effectiveCollapsed && (
                <Dropdown menu={pinnedMenu} trigger={['click']} placement="bottomRight">
                    <span
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 22,
                            height: 22,
                            borderRadius: 4,
                            cursor: 'pointer',
                            opacity: hovered ? 1 : 0,
                            transition: 'opacity 0.15s ease, background 0.15s ease',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#ede9fe';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                        }}
                        title="Pinned Projects"
                    >
                        <RightOutlined style={{ fontSize: 11, color: '#7c3aed' }} />
                    </span>
                </Dropdown>
            )}
        </button>
    );

    if (effectiveCollapsed) {
        return (
            <Tooltip
                key={item.key}
                placement="right"
                title={
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#ffffff' }}>
                            {item.label}
                        </span>
                    </div>
                }
                color="#1e1b4b"
                overlayInnerStyle={{ borderRadius: 8, padding: '5px 10px' }}
            >
                <div style={{ margin: '2px 0' }}>{buttonContent}</div>
            </Tooltip>
        );
    }

    return <div style={{ margin: '2px 0' }}>{buttonContent}</div>;
}

export function InsideSingleSidebar({ L }) {
    const location = useLocation();
    const navigate = useNavigate();
    const can = useCanCheck();
    const user = useAuthStore((state) => state.user);
    const userRole = user?.role || (Array.isArray(user?.roles) ? user?.roles[0] : '');

    const userPermissionKeys = useMemo(() => {
        if (!user?.permissions || !Array.isArray(user.permissions)) return [];
        return user.permissions
            .map((p) => (typeof p === 'object' && p !== null ? p.key : typeof p === 'string' ? p : null))
            .filter(Boolean);
    }, [user?.permissions]);

    const hasPermission = useCallback(
        (permission) => {
            if (!permission) return true;
            const isAdminRole = [
                'Company Admin',
                'Super Admin',
                'company-admin',
                'super-admin',
                'admin',
                'Sub Admin',
                'sub-admin',
            ].includes(userRole);
            if (isAdminRole) return true;

            const checkOne = (p) => {
                if (!p) return true;
                if (typeof p === 'string' && p.endsWith('.')) {
                    return userPermissionKeys.some((userPerm) => typeof userPerm === 'string' && userPerm.startsWith(p));
                }
                return can(p) || userPermissionKeys.includes(p);
            };

            if (Array.isArray(permission)) {
                return permission.some((p) => checkOne(p));
            }

            return checkOne(permission);
        },
        [userRole, userPermissionKeys, can],
    );

    const hasEmployeesView = useMemo(() => {
        return (
            can('hr.employees.view_all') ||
            can('hr.employees.view') ||
            userPermissionKeys.includes('hr.employees.view_all') ||
            userPermissionKeys.includes('hr.employees.view')
        );
    }, [can, userPermissionKeys]);

    const sidebarCollapsed = useUIStore((state) => state.sidebarCollapsed);
    const toggleSidebar = useUIStore((state) => state.toggleSidebar);

    const { pinnedProjects } = usePinnedProjects();

    const sidebarRef = useRef(null);

    // Hover-expand: when the sidebar is collapsed, hovering temporarily shows it expanded
    const [hoverExpanded, setHoverExpanded] = useState(false);

    // Effective collapsed state — false when hovering over a collapsed sidebar
    const effectiveCollapsed = sidebarCollapsed && !hoverExpanded;

    // Track collapsed sections map individually: false/undefined = expanded, true = collapsed
    const [collapsedSections, setCollapsedSections] = useState({});

    const toggleSection = (sectionName, e) => {
        e?.stopPropagation();
        setCollapsedSections((prev) => ({
            ...prev,
            [sectionName]: !prev[sectionName],
        }));
    };

    const activePath = location.pathname;

    const [itemExpanded, setItemExpanded] = useState({
        '/tenant/projects': true
    });

    const isItemActive = (item) => {
        if (activePath === item.key) return true;
        if (item.key === '/tenant/hr/attendance/detail' || item.key === '/tenant/hr/profile/detail') {
            return activePath === '/tenant/hr/attendance/detail' || activePath === '/tenant/hr/profile/detail' || activePath.startsWith('/tenant/hr/attendance/detail') || activePath.startsWith('/tenant/hr/profile/detail');
        }
        if (item.key === '/tenant/projects') {
            const sub = activePath.split('/')[3];
            return activePath === '/tenant/projects' || activePath === '/tenant/projects/overview' || (activePath.startsWith('/tenant/projects/') && sub && !['sprints', 'tasks', 'kanban', 'build', 'release', 'directory', 'submit-to-client'].includes(sub));
        }
        if (item.key === '/tenant/projects/sprints') {
            return activePath === '/tenant/projects/sprints' || activePath === '/tenant/projects/tasks';
        }
        if (item.key === '/tenant/projects/release') {
            return activePath === '/tenant/projects/release' || activePath === '/tenant/projects/submit-to-client';
        }
        if (item.key !== '/tenant/dashboard' && activePath.startsWith(item.key)) return true;
        if (item.children && item.children.some(child => activePath === child.key || activePath.startsWith(child.key))) return true;
        return false;
    };

    const [projectsFlyoutOpen, setProjectsFlyoutOpen] = useState(false);
    const projectsNavRef = useRef(null);
    const flyoutTimerRef = useRef(null);

    const openFlyout = () => {
        if (pinnedProjects.length === 0) return;
        clearTimeout(flyoutTimerRef.current);
        setProjectsFlyoutOpen(true);
    };
    const closeFlyout = () => {
        flyoutTimerRef.current = setTimeout(() => setProjectsFlyoutOpen(false), 120);
    };

    const handleNavClick = (item) => {
        if (item.children && item.children.length > 0) {
            setItemExpanded((prev) => ({
                ...prev,
                [item.key]: !prev[item.key]
            }));
            return;
        }
        navigate(item.key);
    };

    const renderNavSection = (sectionName, items) => {
        const isExpanded = effectiveCollapsed || !sectionName || !collapsedSections[sectionName];

        return (
            <div key={sectionName || 'top'} style={{ marginBottom: 4 }}>
                {/* Professional Grey Line Divider before sections */}
                {sectionName && (
                    <div
                        style={{
                            height: 1,
                            background: '#f1f5f9',
                            margin: effectiveCollapsed ? '8px 4px 6px 4px' : '10px 6px 6px 6px',
                            borderRadius: 1,
                        }}
                    />
                )}

                {sectionName && !effectiveCollapsed && (
                    <div
                        onClick={(e) => toggleSection(sectionName, e)}
                        style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: '#94a3b8',
                            letterSpacing: '0.8px',
                            textTransform: 'uppercase',
                            padding: '6px 6px 6px 6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            userSelect: 'none',
                            transition: 'color 0.15s ease',
                        }}
                    >
                        <span>{sectionName}</span>
                        {isExpanded ? (
                            <MinusOutlined style={{ fontSize: 10, color: '#94a3b8' }} />
                        ) : (
                            <PlusOutlined style={{ fontSize: 10, color: '#94a3b8' }} />
                        )}
                    </div>
                )}

                {/* Animated Section Content */}
                <div
                    style={{
                        maxHeight: isExpanded ? 500 : 0,
                        opacity: isExpanded ? 1 : 0,
                        overflow: 'hidden',
                        transition: 'max-height 0.28s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.22s ease-in-out',
                    }}
                >
                    {items.map((item) => {
                        const IconComp = item.icon;
                        const active = isItemActive(item);
                        const isExpandedItem = itemExpanded[item.key] !== false;

                        let buttonContent;
                        if (item.key === '/tenant/projects') {
                            buttonContent = (
                                <ProjectsNavItem
                                    key={item.key}
                                    item={item}
                                    active={active}
                                    isExpanded={isExpandedItem}
                                    effectiveCollapsed={effectiveCollapsed}
                                    handleNavClick={handleNavClick}
                                    pinnedProjects={pinnedProjects}
                                    navigate={navigate}
                                />
                            );
                        } else {
                            buttonContent = (
                                <button
                                    type="button"
                                    onClick={() => handleNavClick(item)}
                                    style={{
                                        width: '100%',
                                        height: 36,
                                        padding: '0 6px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: effectiveCollapsed ? 'center' : 'flex-start',
                                        gap: 10,
                                        border: 'none',
                                        borderRadius: 8,
                                        background: active ? '#f5f3ff' : 'transparent',
                                        color: active ? '#7c3aed' : '#475569',
                                        fontWeight: active ? 600 : 500,
                                        fontSize: 12.5,
                                        cursor: 'pointer',
                                        transition: 'background 0.15s ease',
                                        outline: 'none',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!active) e.currentTarget.style.background = '#f8fafc';
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!active) e.currentTarget.style.background = 'transparent';
                                    }}
                                >
                                    <div
                                        style={{
                                            width: 28,
                                            height: 28,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <IconComp
                                            style={{
                                                fontSize: 16,
                                                color: active ? '#7c3aed' : '#64748b',
                                            }}
                                        />
                                    </div>
                                    {!effectiveCollapsed && (
                                        <span
                                            style={{
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                flex: 1,
                                                textAlign: 'left',
                                            }}
                                        >
                                            {item.label}
                                        </span>
                                    )}
                                </button>
                            );
                        }

                        if (effectiveCollapsed) {
                            return (
                                <Tooltip
                                    key={item.key}
                                    placement="right"
                                    title={
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            {item.section && (
                                                <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#c084fc', letterSpacing: '0.5px' }}>
                                                    {item.section}
                                                </span>
                                            )}
                                            <span style={{ fontSize: 12, fontWeight: 600, color: '#ffffff' }}>
                                                {item.label}
                                            </span>
                                        </div>
                                    }
                                    color="#1e1b4b"
                                    overlayInnerStyle={{ borderRadius: 8, padding: '5px 10px' }}
                                >
                                    <div style={{ margin: '2px 0' }}>{buttonContent}</div>
                                </Tooltip>
                            );
                        }

                        // Render child items (other items) if any
                        const renderChildren = !effectiveCollapsed && isExpandedItem && item.children && item.children.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: 16, margin: '2px 0' }}>
                                {item.children.map((child) => {
                                    const ChildIcon = child.icon;
                                    const childActive = activePath === child.key;
                                    return (
                                        <button
                                            key={child.key}
                                            type="button"
                                            onClick={() => navigate(child.key)}
                                            style={{
                                                width: '100%',
                                                height: 30,
                                                padding: '0 6px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-start',
                                                gap: 8,
                                                border: 'none',
                                                borderRadius: 6,
                                                background: childActive ? '#f5f3ff' : 'transparent',
                                                color: childActive ? '#7c3aed' : '#64748b',
                                                fontWeight: childActive ? 600 : 500,
                                                fontSize: 11.5,
                                                cursor: 'pointer',
                                                transition: 'background 0.15s ease',
                                                outline: 'none',
                                                margin: '1px 0',
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!childActive) e.currentTarget.style.background = '#f8fafc';
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!childActive) e.currentTarget.style.background = 'transparent';
                                            }}
                                        >
                                            <div style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                <ChildIcon style={{ fontSize: 13, color: childActive ? '#7c3aed' : '#94a3b8' }} />
                                            </div>
                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, textAlign: 'left' }}>
                                                {child.label}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        );

                        return (
                            <div key={item.key} style={{ margin: '2px 0' }}>
                                {buttonContent}
                                {renderChildren}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // Filter items by permission & inject Human Resource button if employee view permission is missing
    const dynamicNavItems = useMemo(() => {
        const filtered = [];

        RAW_NAV_ITEMS.forEach((item) => {
            if (item.section === 'HUMAN RESOURCE') {
                if (item.hrOnlyWithEmployeeView) {
                    if (hasEmployeesView && hasPermission(item.requiredPermission)) {
                        filtered.push(item);
                    }
                } else if (!item.requiredPermission || hasPermission(item.requiredPermission)) {
                    filtered.push(item);
                }
            } else {
                if (!item.requiredPermission || hasPermission(item.requiredPermission)) {
                    filtered.push(item);
                }
            }
        });

        const hasAnyHrItem = filtered.some((item) => item.section === 'HUMAN RESOURCE');
        if (!hasEmployeesView && !hasAnyHrItem) {
            filtered.push({
                key: '/tenant/hr/attendance/detail',
                label: 'Human Resource',
                icon: ApartmentOutlined,
                section: 'HUMAN RESOURCE',
            });
        }

        return filtered;
    }, [hasEmployeesView, hasPermission]);

    // Group items array by sections using Map (omits empty sections)
    const sections = useMemo(() => {
        const result = [];
        const sectionMap = new Map();

        dynamicNavItems.forEach((item) => {
            const secName = item.section || null;
            if (!sectionMap.has(secName)) {
                sectionMap.set(secName, []);
            }
            sectionMap.get(secName).push(item);
        });

        sectionMap.forEach((items, name) => {
            if (items.length > 0) {
                result.push({ name, items });
            }
        });

        return result;
    }, [dynamicNavItems]);

    const userName = L.getUserName?.() || 'Rohail Naveed';
    const userEmail = L.user?.email || 'rohail@codefied.co';
    const userInitials = L.getUserInitials?.() || 'RN';

    // Projects Flyout Panel — rendered as fixed right-side dropdown on hover
    const ProjectsFlyout = () => {
        if (!projectsFlyoutOpen || pinnedProjects.length === 0) return null;
        const ref = projectsNavRef.current;
        const rect = ref ? ref.getBoundingClientRect() : null;
        const top = rect ? rect.top : 120;
        const left = effectiveCollapsed ? 68 : 248;

        return (
            <div
                style={{
                    position: 'fixed',
                    top,
                    left,
                    zIndex: 9999,
                    minWidth: 220,
                    background: '#ffffff',
                    borderRadius: 12,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.08)',
                    border: '1px solid #e8edf5',
                    padding: '8px',
                    animation: 'flyoutIn 0.15s ease-out',
                }}
                onMouseEnter={openFlyout}
                onMouseLeave={closeFlyout}
            >
                <style>{`
                    @keyframes flyoutIn {
                        from { opacity: 0; transform: translateX(-6px); }
                        to   { opacity: 1; transform: translateX(0); }
                    }
                `}</style>

                {pinnedProjects.map((p) => {
                    const pPath = `/tenant/projects?projectId=${encodeURIComponent(p.id)}`;
                    const currentProjectId = new URLSearchParams(location.search).get('projectId') || '';
                    const pActive = location.pathname.startsWith('/tenant/projects') && currentProjectId === p.id;
                    return (
                        <button
                            key={p.id}
                            type="button"
                            onClick={() => { navigate(pPath); setProjectsFlyoutOpen(false); }}
                            style={{
                                width: '100%',
                                padding: '5px 8px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 7,
                                border: 'none',
                                borderRadius: 6,
                                background: pActive ? '#f5f3ff' : 'transparent',
                                cursor: 'pointer',
                                textAlign: 'left',
                                transition: 'background 0.12s ease',
                                marginBottom: 1,
                            }}
                            onMouseEnter={(e) => { if (!pActive) e.currentTarget.style.background = '#f8fafc'; }}
                            onMouseLeave={(e) => { if (!pActive) e.currentTarget.style.background = 'transparent'; }}
                        >
                            <Pin
                                size={11}
                                fill={pActive ? '#7c3aed' : '#cbd5e1'}
                                style={{ color: pActive ? '#7c3aed' : '#cbd5e1', flexShrink: 0 }}
                            />
                            <span style={{
                                fontSize: 12,
                                fontWeight: pActive ? 600 : 400,
                                color: pActive ? '#7c3aed' : '#475569',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>
                                {p.name}
                            </span>
                        </button>
                    );
                })}
            </div>
        );
    };

    return (
        <>
        <aside
            ref={sidebarRef}
            style={{
                width: effectiveCollapsed ? 60 : 240,
                minWidth: effectiveCollapsed ? 60 : 240,
                height: '100vh',
                flexShrink: 0,
                background: '#ffffff',
                borderRight: '1px solid var(--ant-color-border-secondary, #f1f5f9)',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
                transition: 'width 0.28s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
                zIndex: 10,
                userSelect: 'none',
                overflow: 'hidden',
            }}
            onMouseEnter={() => { if (sidebarCollapsed) setHoverExpanded(true); }}
        >
            <style>{`
                .hide-scrollbar::-webkit-scrollbar {
                    display: none;
                }
                .hide-scrollbar {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>
            {/* Header: Logo + Title + Shrink/Expand Toggle */}
            <div
                style={{
                    height: 56,
                    padding: effectiveCollapsed ? '0 8px' : '0 12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid #f8fafc',
                    flexShrink: 0,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: effectiveCollapsed ? 4 : 10, cursor: 'pointer' }} onClick={() => navigate('/tenant/dashboard')}>
                    <img
                        src={logoSrc}
                        alt="Sprinto"
                        style={{ width: 28, height: 28, objectFit: 'contain', flexShrink: 0 }}
                    />
                    {!effectiveCollapsed && (
                        <span style={{ fontSize: 18, fontWeight: 700, color: '#7c3aed', letterSpacing: '-0.5px' }}>
                            sprinto
                        </span>
                    )}
                </div>
                {effectiveCollapsed ? (
                    <Tooltip placement="right" title="Expand sidebar" color="#1e1b4b">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setHoverExpanded(false);
                                useUIStore.getState().setSidebarCollapsed(false);
                            }}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#94a3b8',
                                cursor: 'pointer',
                                padding: 6,
                                borderRadius: 6,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'color 0.15s ease',
                            }}
                        >
                            <MenuUnfoldOutlined style={{ fontSize: 15 }} />
                        </button>
                    </Tooltip>
                ) : (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setHoverExpanded(false);
                            useUIStore.getState().setSidebarCollapsed(true);
                        }}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#94a3b8',
                            cursor: 'pointer',
                            padding: 6,
                            borderRadius: 6,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'color 0.15s ease',
                        }}
                        title="Shrink sidebar"
                    >
                        <MenuFoldOutlined style={{ fontSize: 15 }} />
                    </button>
                )}
            </div>

            {/* Navigation Body (Scrollable) */}
            <div
                className="hide-scrollbar"
                style={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    padding: '4px 8px',
                }}
            >
                {sections.map((sec) => renderNavSection(sec.name, sec.items))}
            </div>

            {/* User Profile Footer */}
            <div
                style={{
                    padding: '12px',
                    borderTop: '1px solid #f1f5f9',
                    flexShrink: 0,
                }}
            >
                <Dropdown
                    menu={{
                        items: L.userMenuItems || [],
                        onClick: L.handleUserMenuClick,
                    }}
                    trigger={['click']}
                    placement="topRight"
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '4px 6px',
                            borderRadius: 10,
                            cursor: 'pointer',
                            transition: 'background 0.15s ease',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#f8fafc')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                            {/* Avatar with Online Indicator */}
                            <div style={{ position: 'relative', flexShrink: 0 }}>
                                <div
                                    style={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: '50%',
                                        background: 'linear-gradient(135deg, #ea580c 0%, #f97316 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#ffffff',
                                        fontWeight: 700,
                                        fontSize: 11,
                                    }}
                                >
                                    {userInitials}
                                </div>
                                <span
                                    style={{
                                        position: 'absolute',
                                        bottom: 0,
                                        right: 0,
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        background: '#10b981',
                                        border: '2px solid #ffffff',
                                    }}
                                />
                            </div>

                            {!effectiveCollapsed && (
                                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                    <span
                                        style={{
                                            fontSize: 12.5,
                                            fontWeight: 600,
                                            color: '#1e293b',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            lineHeight: '1.2',
                                        }}
                                    >
                                        {userName}
                                    </span>
                                    <span
                                        style={{
                                            fontSize: 11,
                                            color: '#94a3b8',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            lineHeight: '1.2',
                                            marginTop: 2,
                                        }}
                                    >
                                        {userEmail}
                                    </span>
                                </div>
                            )}
                        </div>

                        {!effectiveCollapsed && <MoreOutlined style={{ fontSize: 16, color: '#94a3b8' }} />}
                    </div>
                </Dropdown>
            </div>
        </aside>
        <ProjectsFlyout />
        <CreateBugReportModal
            open={L.createBugModalOpen}
            onCancel={() => L.setCreateBugModalOpen(false)}
        />
        </>
    );
}

export default InsideSingleSidebar;
